import React, {Fragment, useEffect, useRef, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from './message-list';
import ProductImage from './ProductImage.jsx';
import Device from '@core/scripts/components/device';
import Landmark from '@core/scripts/components/landmark-adapter';
import Messages from '@core/scripts/components/messages';
import {getBestImageIndex, isLazyLoaded} from '../helper/image-slider-utils';
import AppGallery from './actions/app-gallery';
import ProductImageWishlistHeart from './ProductImageWishlistHeart.jsx';
import {yieldToMain} from "../helper/ProductPageHelper.jsx";
import ProductVideo from "./ProductVideo.jsx";
import {Carousel, Icon, Grid} from "@bonprix/design-system";
import ProductImageWithZoom from "./product-image/ProductImageWithZoom.jsx";
import ModelContainer from '@core/scripts/components/model-container';

// 5 is the good value for recognizing finger movements on the touch screen as swipe movements
const SWIPER_THRESHOLD_RIGHT = 10;
const SWIPER_THRESHOLD_LEFT = -10;
const MAX_DIFF_TOUCH_Y = 50;
const IMAGE_WIDTH = 100;

let indicesOfLoadedImages = [];

const isLivingAreaAssortment = ModelContainer.get('isLivingAreaAssortment');

export default function ProductImageSlider({product, texts, initialSlideIndex, productImageDimensions, fullWidth, onStyleChange}) {

    const [isButtonClickIgnored, setIsButtonClickIgnored] = useState(false);
    const [isSlidingIgnored, setIsSlidingIgnored] = useState(false);
    const [landscapeMode, setLandscapeMode] = useState(Device.isLandscapeMode());
    const [touchStartX, setTouchStartX] = useState(null);

    const [touchStartY, setTouchStartY] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const [touchEndY, setTouchEndY] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

    const [currentItem, setCurrentItem] = useState(product.currentItem);
    const [currentItemOption, setCurrentItemOption] = useState(product.currentItemOption);
    const currentInitialSlideIndex = currentItem.video && initialSlideIndex > 0 ? initialSlideIndex + 1 : initialSlideIndex;
    const [slideIndex, setSlideIndex] = useState(currentInitialSlideIndex);
    const [videoState, setVideoState] = useState('stop');

    const transitionEnabled = useRef(true);

    const sliderProgressRef = useRef(null);
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);

    useEffect(() => {
        AppGallery.addOnClosedListener((newSlideIndex) => {
            if (slideIndex !== newSlideIndex) {
                transitionEnabled.current = false;
                setSlideIndex(newSlideIndex);
            }
        });
        const subscriptions = [
            MessageBroker.subscribe(ProductMessages.CHANGED, ({item, itemOption}) => {
                setCurrentItemOption(itemOption);
                if (item !== undefined) {
                    setCurrentItem(item);
                    indicesOfLoadedImages = [];

                    if (!isSlideIndexValid(slideIndex, item)) {
                        transitionEnabled.current = false;
                        setSlideIndex(getBestImageIndex(item));
                    }
                }
            }),
            MessageBroker.subscribe(Messages.WINDOW.ORIENTATION_CHANGED, () => {
                setLandscapeMode(Device.isLandscapeMode());
            }),
            MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, ({majorBreakpointChanged}) => {
                if (majorBreakpointChanged) {
                    setLandscapeMode(Device.isLandscapeMode());
                }
            }),
        ];

        return () => subscriptions.forEach((subscription) => MessageBroker.unsubscribe(subscription));
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!transitionEnabled.current) {
            //enable button click
            setIsButtonClickIgnored(false);
            //reactivate transition after reinitialization of image loop
            if (slideIndex !== -1 && slideIndex !== images.length && images.length !== 1) {
                transitionEnabled.current = true;
            }
        }
        if (currentItem.video) {
            handleVideo(slideIndex);
        }
    }, [slideIndex]);

    const handleVideo = (index) => {
        if (index === 1) {
            setVideoState('play');
        } else {
            setVideoState('stop');
        }
    };

    const isSlideIndexValid = (slideIndex, item) => {
        return slideIndex >= 0 && slideIndex < item.images?.length;
    };

    const onSliderButtonClick = (isSlideBack) => {
        // to prevent button clicks that are faster than transition
        if (isButtonClickIgnored) {
            return;
        }
        slideToSpecificImage(findNewImageIndex(isSlideBack));
    };

    const slideToSpecificImage = (index) => {
        yieldToMain(() => {
            setIsButtonClickIgnored(true);
            setSlideIndex(index);
            Landmark.sendClickLandmark(`slide\\${index + 1}`);
        });
    };

    const findNewImageIndex = (isSlideBack) => {
        return isSlideBack ? slideIndex - 1 : slideIndex + 1;
    };

    const isTouchOn = (ref, e) => ref.current.contains(e.target);

    const isTouchOnImage = (e) => {
        return !(isTouchOn(prevButtonRef, e) || isTouchOn(nextButtonRef, e) || isTouchOn(sliderProgressRef, e));
    };

    const handleTouchStart = (e) => {
        if (isTouchOnImage(e)) {
            setTouchStartX(e.targetTouches[0].clientX);
            setTouchStartY(e.targetTouches[0].clientY);
        }
    };

    const handleTouchMove = (e) => {
        if (isTouchOnImage(e)) {
            setTouchEndX(e.targetTouches[0].clientX);
            setTouchEndY(e.targetTouches[0].clientY);
        }
    };

    const handleTouchEnd = (e) => {
        if (isTouchOnImage(e)) {
            const diffX = touchStartX - (touchEndX || touchStartX);
            const diffY = touchStartY - (touchEndY || touchStartY);
            if (
                !isSlidingIgnored &&
                diffX > SWIPER_THRESHOLD_RIGHT &&
                Math.abs(diffY) < MAX_DIFF_TOUCH_Y &&
                images.length > 1
            ) {
                slideToSpecificImage(findNewImageIndex(false));
                setIsSlidingIgnored(true);
            }
            if (
                !isSlidingIgnored &&
                diffX < SWIPER_THRESHOLD_LEFT &&
                Math.abs(diffY) < MAX_DIFF_TOUCH_Y &&
                images.length > 1
            ) {
                slideToSpecificImage(findNewImageIndex(true));
                setIsSlidingIgnored(true);
            }
            setTouchStartX(null);
            setTouchStartY(null);
            setTouchEndX(null);
            setTouchEndY(null);
        }
    };

    const handleTransitionEnd = () => {
        setIsButtonClickIgnored(false);
        setIsSlidingIgnored(false);
        if (slideIndex === -1) {
            transitionEnabled.current = false;
            setSlideIndex(images.length - 1);
        } else if (slideIndex === images.length) {
            transitionEnabled.current = false;
            setSlideIndex(0);
        }
    };

    const shouldBeLazyLoaded = (index) => {
        const lazy = isLazyLoaded(index, images, slideIndex, indicesOfLoadedImages);
        if (!lazy && !indicesOfLoadedImages.includes(index)) {
            indicesOfLoadedImages.push(index);
        }

        return lazy;
    };

    const addVideoToSliderImagesIfAvailable = (images, video) => {
        if (video && !images.some(image => image.type === 'VIDEO')) {
            video.type = 'VIDEO';
            images.splice(1, 0, video);
        }
    };

    const {images, video, itemNumber} = currentItem;
    addVideoToSliderImagesIfAvailable(images, video);
    const firstImage = images[0];
    const lastImage = images[images.length - 1];
    const soldOut = currentItem?.availability?.soldOut;
    const slidable = images.length > 1 && !soldOut;
    const currentSlideIndex = (slideIndex + images.length) % images.length;
    const imageZoomAllowed = currentItem.imageZoomAllowed && !soldOut;
    const sliderContentStyle = {
        transform: `translateX(-${(slideIndex + 1) * IMAGE_WIDTH}%)`,
    };

    if (!fullWidth && !slidable) {
        return (
            <div className="product-image-slider">
                <div className="product-image-slider__content-wrapper">
                    <div className="product-image-slider__content">
                        <div className="product-image" style={{width: IMAGE_WIDTH + '%'}}>
                            <ProductImage
                                image={firstImage}
                                images={images}
                                zoomAllowed={imageZoomAllowed}
                                dimensions={productImageDimensions}
                                soldOut={soldOut}
                                texts={texts}
                                index={'slidable-productimage'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isArticleSoldOutOrHaveOneImage = images.length===1 || soldOut;

    useEffect(() => {
        onStyleChange(isArticleSoldOutOrHaveOneImage || isLivingAreaAssortment);
    }, [isArticleSoldOutOrHaveOneImage, isLivingAreaAssortment]);

    if (fullWidth && isArticleSoldOutOrHaveOneImage) {
        return (
            <div className="image__container_soldout__or__have_OneImage">
                <ProductImage
                    image={firstImage}
                    images={images}
                    zoomAllowed={imageZoomAllowed}
                    lazy={false}
                    soldOut={soldOut}
                    slideIndex={slideIndex}
                    texts={texts}
                    dimensions={productImageDimensions}
                    fullwidth={fullWidth}
                />
            </div>
        );
    }

    function renderImageSliderWithFullWith() {
        return (
            <div className="image__container_soldout__or__have_OneImage">
                <Carousel
                    arrows
                    gap={0}
                    label="label"
                    onMove={() => {
                    }}
                    pagination
                    perPage={1}
                    rewind
                >
                    {images.map((image, index) => {
                        if (image.type === 'VIDEO') {
                            return (
                                <Carousel.Slide key={`video-${index}`}>
                                    <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                </Carousel.Slide>
                            )
                        }
                        return (
                            <Carousel.Slide key={`image-${index}`}>
                                <ProductImageWithZoom
                                    key={index}
                                    image={image}
                                    soldOut={soldOut}
                                    texts={texts}
                                    imageZoomAllowed={imageZoomAllowed}
                                />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            </div>
        )
    }

    if (fullWidth) {
        return (
            <div>
                {isMobileView ? (
                    <>
                        <div className="image__container">
                            <Carousel
                                arrows
                                gap={0}
                                label="label"
                                onMove={(index) => setSlideIndex(index)}
                                pagination
                                perPage={1}
                                rewind
                            >
                                {images.map((image, index) => {
                                    if (image.type === 'VIDEO') {
                                        return (
                                            <Carousel.Slide key={currentItem.id +"-"+ index}>
                                                <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                            </Carousel.Slide>
                                        )
                                    }
                                    return (
                                        <Carousel.Slide key={currentItem.id +"-"+ index}>
                                            <ProductImage
                                                image={image}
                                                images={images}
                                                zoomAllowed={imageZoomAllowed}
                                                lazy={shouldBeLazyLoaded(index)}
                                                slideIndex={slideIndex}
                                                dimensions={productImageDimensions}
                                                index={index}/>
                                        </Carousel.Slide>
                                    );
                                })}
                            </Carousel>
                            <div className="product__wishlistHeart__full_width">
                                <ProductImageWishlistHeart
                                    product={product}
                                    currentItem={currentItem}
                                    currentItemOption={currentItemOption}
                                    fullWidth={fullWidth}
                                />
                            </div>
                        </div>
                    </>

                ) : (
                    !isLivingAreaAssortment ? (
                        <Grid disableGutters>
                            {images.map((image, index) => {
                                if (image.type === 'VIDEO') {
                                    return (
                                        <Grid.Item s={12} xxl={6}>
                                            <div className="product-image" key={index} style={{width: IMAGE_WIDTH + '%'}}>
                                                <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                            </div>
                                        </Grid.Item>
                                    );
                                }
                                return (
                                    <Grid.Item s={12} xxl={6}>
                                        <ProductImageWithZoom
                                            key={index}
                                            image={image}
                                            soldOut={soldOut}
                                            texts={texts}
                                            imageZoomAllowed={imageZoomAllowed}
                                        />
                                    </Grid.Item>
                                );
                            })}
                        </Grid>
                    ) : (
                        renderImageSliderWithFullWith()
                    )
                )}
            </div>
        )
    }


    return (
        <div className="product-image-slider">
            <div
                className="product-image-slider__content-wrapper"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className={
                        'product-image-slider__content' +
                        (transitionEnabled.current ? ' transition-active' : '')
                    }
                    style={sliderContentStyle}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {/* last image is doubled for loop sliding*/}
                    <div className="product-image" key={images.length + 1} style={{width: IMAGE_WIDTH + '%'}}>
                        <ProductImage
                            image={lastImage}
                            images={images}
                            zoomAllowed={imageZoomAllowed}
                            lazy={shouldBeLazyLoaded(images.length - 1)}
                            slideIndex={slideIndex}
                            dimensions={productImageDimensions}
                            index={images.length + 1}
                        />
                    </div>
                    {images.map((image, index) => {
                        if (image.type === 'VIDEO') {
                            return (
                                <div className="product-image" key={index} style={{width: IMAGE_WIDTH + '%'}}>
                                    <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                </div>
                            )
                        }
                        return <div className="product-image" key={index} style={{width: IMAGE_WIDTH + '%'}}>
                            <ProductImage
                                image={image}
                                images={images}
                                zoomAllowed={imageZoomAllowed}
                                lazy={shouldBeLazyLoaded(index)}
                                slideIndex={slideIndex}
                                dimensions={productImageDimensions}
                                index={index}
                            />
                        </div>
                    })}
                    {/* first image is doubled for loop sliding */}
                    <div className="product-image" key={images.length + 2} style={{width: IMAGE_WIDTH + '%'}}>
                        <ProductImage
                            image={firstImage}
                            images={images}
                            zoomAllowed={imageZoomAllowed}
                            lazy={shouldBeLazyLoaded(0)}
                            slideIndex={slideIndex}
                            dimensions={productImageDimensions}
                            index={images.length + 2}
                        />
                    </div>
                </div>
                {slidable && (
                    <>
                        <div ref={prevButtonRef} onClick={() => onSliderButtonClick(true)}
                             className="product-image-slider__button -prev">
                            <Icon variant='arrow-left' attributes={{'aria-label': texts['bff_ada_product_image_slider_previous_description']}}/>
                        </div>
                        <div ref={nextButtonRef} onClick={() => onSliderButtonClick(false)} className="product-image-slider__button -next">
                            <Icon variant='arrow-right' attributes={{'aria-label': texts['bff_ada_product_image_slider_next_description']}}/>
                        </div>
                    </>
                )}
            </div>
            {Device.isTouch() && (
                <ProductImageWishlistHeart
                    product={product}
                    currentItem={currentItem}
                    currentItemOption={currentItemOption}
                />
            )}
            <div ref={sliderProgressRef}  className="slider-progress__disabled-bar"></div>
            <div
                className="slider-progress__active-bar"
                style={{
                    marginLeft: `${(currentSlideIndex) / images.length * 100}%`,
                    width: `${100 / images.length}%`
                }}
            ></div>
        </div>
    );
}
